import type { components } from '@social-studio/api-client';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { ContentSummary } from '../content/content-api';
import { ListLoadError } from '../knowledge/properties-api';

export type Publication = components['schemas']['PublicationDto'];

/** Queue page size: the queue is short by nature; the API allows 200 per page. */
const QUEUE_LIMIT = 200;
/** The publications API accepts a span of at most 62 days (`range_too_large`). */
const HORIZON_DAYS = 62;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface QueueEntry {
  readonly item: ContentSummary;
  /** The next planned or scheduled publication (UTC), if any within the horizon. */
  readonly nextPublicationAt: string | null;
}

/**
 * Planned/scheduled publications from now on, earliest first (module 60's public list; no special route).
 * The tenant-wide list stays within the API's 62-day span; one item's list has no upper bound, so a post
 * planned further ahead (e.g. by the year planner) still finds its next publication.
 */
async function fetchUpcomingPublications(filter: { content_item_id?: string } = {}): Promise<Publication[]> {
  const now = Date.now();
  const { data, response } = await api.GET('/v1/publications', {
    params: {
      query: {
        ...filter,
        status: ['planned', 'scheduled'],
        scheduled_at_from: new Date(now).toISOString(),
        ...(filter.content_item_id ? {} : { scheduled_at_to: new Date(now + HORIZON_DAYS * DAY_MS).toISOString() }),
        limit: QUEUE_LIMIT,
      },
    },
  });
  if (!response.ok || !data) throw new ListLoadError(response.status);
  return data.data;
}

const byDate = (a: string | null, b: string | null) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a < b ? -1 : 1);

/**
 * Sorts in-review items by next planned publication, then desired date (spec
 * 50 section 6.2); items with neither come last, then by title for a stable order.
 */
export function orderQueue(items: readonly ContentSummary[], publications: readonly Publication[]): QueueEntry[] {
  const next = new Map<string, string>();
  for (const publication of publications) {
    const known = next.get(publication.content_item_id);
    if (known === undefined || publication.scheduled_at < known) next.set(publication.content_item_id, publication.scheduled_at);
  }
  return items
    .map((item) => ({ item, nextPublicationAt: next.get(item.id) ?? null }))
    .sort(
      (a, b) =>
        byDate(a.nextPublicationAt, b.nextPublicationAt) ||
        byDate(a.item.desired_date, b.item.desired_date) ||
        a.item.title.localeCompare(b.item.title) ||
        byDate(a.item.id, b.item.id),
    );
}

export async function fetchQueue(): Promise<QueueEntry[]> {
  const { data, response } = await api.GET('/v1/content-items', { params: { query: { status: ['in_review'], limit: QUEUE_LIMIT } } });
  if (!response.ok || !data) throw new ListLoadError(response.status);
  if (data.data.length === 0) return [];
  return orderQueue(data.data, await fetchUpcomingPublications());
}

/** Everything waiting for review (spec 50 section 6.2): `GET /v1/content-items?status=in_review` plus module 60's publications. */
export function useReviewQueueQuery() {
  return useQuery({ queryKey: ['content-items', 'review-queue'] as const, queryFn: fetchQueue, retry: false });
}

/**
 * The item's upcoming publications, used to pick the default language tab of
 * the review screen. Best effort: a failure only means the first tab is shown.
 */
export function useUpcomingPublicationsQuery(contentItemId: string) {
  return useQuery({
    queryKey: ['publications', 'upcoming', contentItemId] as const,
    queryFn: () => fetchUpcomingPublications({ content_item_id: contentItemId }),
    retry: false,
  });
}
